// Phase 44.31.15 — Admin · Sezione "Compliance / Rischio duplicati" per driver detail.
// Riusabile in AdminDriverDetail. Mostra:
//  - badge AVS se earnings_ytd > soglia
//  - verifiche identità/IBAN/veicolo
//  - mismatch nome account vs intestatario IBAN
//  - lista duplicati con campo coincidente, link a profili, azioni confirm/dismiss
//  - earnings aggregati per identità (IBAN/phone/plate/license/doc/email)
//  - toggle manual_review + payout_block con motivo
//  - textarea note interne admin
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, ShieldAlert, ShieldCheck, CheckCircle2, XCircle,
  Eye, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime } from "@/lib/format";

const FIELD_LABELS = {
  iban: "IBAN",
  phone: "Telefono",
  email: "Email",
  plate: "Targa veicolo",
  license: "Numero patente",
  doc_number: "Numero documento",
};

export default function AdminDriverComplianceSection({ driverId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // string | null
  const [reviewModal, setReviewModal] = useState(null);     // {reason} or null
  const [blockModal, setBlockModal] = useState(null);       // {reason} or null
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/admin/drivers/${driverId}/compliance-check`);
      setData(d);
      setNotes(d?.state?.compliance_notes || "");
      setNotesDirty(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [driverId]);

  const toggleManualReview = async (required, reason = null) => {
    setSaving("review");
    try {
      await api.patch(`/admin/drivers/${driverId}/compliance/manual-review`, { required, reason });
      toast.success(required ? "Manual review attivato" : "Manual review tolto");
      setReviewModal(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(null); }
  };

  const togglePayoutBlock = async (blocked, reason = null) => {
    setSaving("payout");
    try {
      await api.patch(`/admin/drivers/${driverId}/compliance/payout-block`, { blocked, reason });
      toast.success(blocked ? "Payout bloccato" : "Payout sbloccato");
      setBlockModal(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(null); }
  };

  const saveNotes = async () => {
    setSaving("notes");
    try {
      await api.patch(`/admin/drivers/${driverId}/compliance/notes`, { notes });
      toast.success("Note salvate");
      setNotesDirty(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(null); }
  };

  const resolveDuplicate = async (otherId, status, reason = null) => {
    setSaving(`dup-${otherId}`);
    try {
      await api.post(`/admin/drivers/${driverId}/compliance/duplicate-resolve`, {
        other_driver_id: otherId, status, reason,
      });
      toast.success(status === "confirmed" ? "Duplicato confermato" : "Duplicato ignorato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(null); }
  };

  if (loading) {
    return (
      <section className="card-soft p-5 sm:p-6" data-testid="admin-driver-compliance-section-loading">
        <p className="overline">Compliance · rischio duplicati</p>
        <p className="text-xs text-dapnow-muted mt-2 inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Analisi in corso…
        </p>
      </section>
    );
  }
  if (!data) return null;

  const { state, identifiers, name_iban_match,
          duplicates, earnings_aggregated_by_identity, verification } = data;

  const hasUnresolvedDup = duplicates.some((d) => !d.resolution);

  return (
    <section className="card-soft p-5 sm:p-6 space-y-4" data-testid="admin-driver-compliance-rischio">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="overline">Compliance · rischio duplicati</p>
          <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">
            Anti-frode driver
          </h3>
        </div>
        {(hasUnresolvedDup || state.manual_review_required || state.payout_blocked) && (
          <span className="inline-flex items-center gap-1 pill bg-amber-50 text-amber-800 text-[10px]" data-testid="admin-compliance-pill-attention">
            <AlertTriangle className="h-3.5 w-3.5" /> Attenzione
          </span>
        )}
      </div>

      {/* Verifiche identità */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <VerifyChip ok={verification.identity_verified} label="Documento identità" />
        <VerifyChip ok={verification.iban_verified} label="IBAN verificato" />
        <VerifyChip ok={verification.vehicle_verified} label="Veicolo verificato" />
        <VerifyChip ok={verification.compliance_verified_by_admin} label="Compliance admin" />
      </div>

      {/* Riepilogo identificatori (con preview IBAN mascherato) */}
      <div className="rounded-xl border border-dapnow-line p-3 text-xs space-y-1" data-testid="admin-compliance-identifiers">
        <p className="overline text-[10px]">Identificatori chiave</p>
        <KV k="Nome" v={data.driver_name} />
        <KV k="N°" v={data.driver_number || "—"} />
        <KV k="IBAN" v={identifiers.iban_preview ? <span className="font-mono">{identifiers.iban_preview}</span> : "—"} />
        <KV k="Intestatario IBAN" v={
          <span className={name_iban_match === false ? "text-dapnow-red font-semibold" : ""}>
            {identifiers.payout_account_holder || "—"}
            {name_iban_match === false && " ⚠"}
          </span>
        } />
        <KV k="Targa" v={identifiers.plate || "—"} />
        <KV k="Patente" v={identifiers.license_number || "—"} />
        <KV k="Documento" v={identifiers.doc_number || "—"} />
        <KV k="Telefono" v={identifiers.phone || "—"} />
        <KV k="Email" v={identifiers.email || "—"} />
        {name_iban_match === false && (
          <p className="text-[11px] text-dapnow-red mt-1">
            Nome account non combacia con intestatario IBAN. Richiedi conferma documento prima del payout.
          </p>
        )}
      </div>

      {/* Lista duplicati */}
      <div data-testid="admin-compliance-duplicates">
        <p className="overline text-[10px] mb-2">Possibili profili duplicati ({duplicates.length})</p>
        {duplicates.length === 0 ? (
          <p className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" /> Nessun campo identificativo condiviso con altri driver.
          </p>
        ) : (
          <ul className="space-y-2">
            {duplicates.map((d) => (
              <li
                key={d.driver_id}
                data-testid={`admin-compliance-duplicate-${d.driver_id}`}
                className={`rounded-xl border p-3 ${
                  d.resolution === "confirmed" ? "border-red-300 bg-red-50/40"
                    : d.resolution === "dismissed" ? "border-zinc-200 bg-zinc-50"
                    : "border-amber-300 bg-amber-50/30"
                }`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold tracking-tight inline-flex items-center gap-1.5">
                      {d.name}{d.driver_number && <span className="font-mono text-[10px] text-dapnow-muted">{d.driver_number}</span>}
                      {d.payout_blocked && <span className="pill bg-red-50 text-red-700 text-[10px]">Payout blocked</span>}
                      {d.manual_review_required && <span className="pill bg-amber-50 text-amber-800 text-[10px]">Manual review</span>}
                    </p>
                    <p className="text-[11px] text-dapnow-muted">
                      {d.email} {d.phone && `· ${d.phone}`}
                    </p>
                    <ul className="mt-1.5 flex flex-wrap gap-1">
                      {d.matches.map((m) => (
                        <li key={m} className="pill bg-white border border-dapnow-line text-[10px]">
                          {m}
                        </li>
                      ))}
                    </ul>
                    {d.resolution && (
                      <p className="text-[11px] mt-1.5 text-dapnow-muted">
                        {d.resolution === "confirmed" ? "🔴 Confermato duplicato" : "⚪️ Ignorato"} da {d.resolution_by || "—"} · {formatDateTime(d.resolution_at)}
                        {d.resolution_reason && <span className="block italic">"{d.resolution_reason}"</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Link
                      to={`/admin/drivers/${d.driver_id}`}
                      data-testid={`admin-compliance-open-driver-${d.driver_id}`}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-dapnow-line bg-white text-[10px] font-semibold hover:border-dapnow-blue"
                    >
                      <Eye className="h-3 w-3" /> Apri scheda
                    </Link>
                    {!d.resolution && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const reason = window.prompt("Motivo conferma duplicato (richiesto):", "");
                            if (reason && reason.trim()) resolveDuplicate(d.driver_id, "confirmed", reason.trim());
                          }}
                          disabled={saving === `dup-${d.driver_id}`}
                          data-testid={`admin-compliance-confirm-dup-${d.driver_id}`}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-lg bg-red-600 text-white text-[10px] font-semibold hover:bg-red-700 disabled:opacity-50"
                        >
                          Conferma duplicato
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const reason = window.prompt("Motivo per ignorare (opzionale):", "Falso positivo");
                            if (reason !== null) resolveDuplicate(d.driver_id, "dismissed", reason.trim() || null);
                          }}
                          disabled={saving === `dup-${d.driver_id}`}
                          data-testid={`admin-compliance-dismiss-dup-${d.driver_id}`}
                          className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-dapnow-line bg-white text-[10px] font-semibold hover:border-dapnow-ink disabled:opacity-50"
                        >
                          Ignora
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Earnings aggregati per identità */}
      {Object.keys(earnings_aggregated_by_identity).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3" data-testid="admin-compliance-earnings-aggregated">
          <p className="overline text-[10px] mb-2">Importi annui per indicatori condivisi</p>
          <ul className="space-y-1.5 text-xs">
            {Object.entries(earnings_aggregated_by_identity).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between gap-3">
                <span>
                  <strong>{FIELD_LABELS[k] || k}</strong>: {v.drivers_count} profili
                  <span className="text-[10px] text-dapnow-muted ml-1">({v.value_normalised_preview})</span>
                </span>
                <span className="font-mono font-semibold">{formatCHF(v.total_ytd_chf)}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-amber-800 mt-2">
            Gli indicatori condivisi, inclusa la targa, non dimostrano che i profili appartengano alla stessa persona. Gli importi sono storici e da riconciliare; non determinano obblighi AVS.
          </p>
        </div>
      )}

      {/* Stato manual_review + payout_block */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="admin-compliance-flags">
        {/* Manual review */}
        <div className={`rounded-xl border p-3 ${state.manual_review_required ? "border-amber-300 bg-amber-50/40" : "border-dapnow-line bg-white"}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold inline-flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5" /> Manual review
            </p>
            <span className={`pill text-[10px] ${state.manual_review_required ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-dapnow-muted"}`}>
              {state.manual_review_required ? "Attivo" : "OFF"}
            </span>
          </div>
          {state.manual_review_required ? (
            <>
              {state.manual_review_reason && <p className="text-[11px] text-dapnow-muted mt-1 italic">"{state.manual_review_reason}"</p>}
              <p className="text-[10px] text-dapnow-muted">
                Da {state.manual_review_set_by || "—"} · {formatDateTime(state.manual_review_set_at)}
              </p>
              <button
                type="button"
                onClick={() => toggleManualReview(false)}
                disabled={saving === "review"}
                data-testid="admin-compliance-review-clear"
                className="mt-2 inline-flex h-8 px-3 rounded-lg border border-dapnow-line bg-white text-[11px] font-semibold hover:border-dapnow-ink disabled:opacity-50"
              >
                {saving === "review" ? "…" : "Togli flag"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setReviewModal({ reason: "" })}
              data-testid="admin-compliance-review-set"
              className="mt-2 inline-flex h-8 px-3 rounded-lg bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700"
            >
              Imposta manual review
            </button>
          )}
          <p className="text-[10px] text-dapnow-muted mt-2">
            Non blocca l'operatività driver (può ancora accettare ordini).
          </p>
        </div>

        {/* Payout block */}
        <div className={`rounded-xl border p-3 ${state.payout_blocked ? "border-red-300 bg-red-50/40" : "border-dapnow-line bg-white"}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold inline-flex items-center gap-1">
              <XCircle className="h-3.5 w-3.5" /> Payout
            </p>
            <span className={`pill text-[10px] ${state.payout_blocked ? "bg-red-100 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
              {state.payout_blocked ? "Bloccato" : "OK"}
            </span>
          </div>
          {state.payout_blocked ? (
            <>
              {state.payout_blocked_reason && <p className="text-[11px] text-dapnow-muted mt-1 italic">"{state.payout_blocked_reason}"</p>}
              <p className="text-[10px] text-dapnow-muted">
                Da {state.payout_blocked_by || "—"} · {formatDateTime(state.payout_blocked_at)}
              </p>
              <button
                type="button"
                onClick={() => togglePayoutBlock(false)}
                disabled={saving === "payout"}
                data-testid="admin-compliance-payout-unblock"
                className="mt-2 inline-flex h-8 px-3 rounded-lg border border-dapnow-line bg-white text-[11px] font-semibold hover:border-dapnow-ink disabled:opacity-50"
              >
                {saving === "payout" ? "…" : "Sblocca payout"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setBlockModal({ reason: "" })}
              data-testid="admin-compliance-payout-block"
              className="mt-2 inline-flex h-8 px-3 rounded-lg bg-red-600 text-white text-[11px] font-semibold hover:bg-red-700"
            >
              Blocca payout
            </button>
          )}
        </div>
      </div>

      {/* Note interne admin */}
      <div data-testid="admin-compliance-notes">
        <label className="overline text-[10px]">Note interne admin</label>
        <textarea
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
          rows={3}
          maxLength={4000}
          placeholder="Es. Conferma fiscale richiesta a fiduciario. Documenti aggiuntivi necessari…"
          data-testid="admin-compliance-notes-textarea"
          className="mt-1 w-full rounded-lg border border-dapnow-line bg-white p-2 text-xs focus:border-dapnow-blue focus:ring-1 focus:ring-dapnow-blue/20 outline-none"
        />
        {notesDirty && (
          <button
            type="button"
            onClick={saveNotes}
            disabled={saving === "notes"}
            data-testid="admin-compliance-notes-save"
            className="mt-2 btn-primary h-9 px-4 text-xs"
          >
            {saving === "notes" ? "Salvataggio…" : "Salva note"}
          </button>
        )}
      </div>

      {/* Modali */}
      {reviewModal && (
        <ReasonModal
          title="Attiva manual review"
          description="Aggiungi un motivo per la review manuale di questo driver."
          placeholder="Es. Possibile duplicato IBAN, verifica documento"
          onCancel={() => setReviewModal(null)}
          onConfirm={(reason) => toggleManualReview(true, reason)}
          confirmLabel="Attiva review"
          required={false}
        />
      )}
      {blockModal && (
        <ReasonModal
          title="Blocca payout"
          description="Specifica il motivo del blocco. Il driver continuerà a operare, ma i payout saranno bloccati."
          placeholder="Es. Verifica nome account vs IBAN"
          onCancel={() => setBlockModal(null)}
          onConfirm={(reason) => togglePayoutBlock(true, reason)}
          confirmLabel="Blocca payout"
          required={true}
        />
      )}
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-[10px] uppercase tracking-wider text-dapnow-muted">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

function VerifyChip({ ok, label }) {
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-dapnow-muted"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      <span className="text-[11px] font-semibold">{label}</span>
    </div>
  );
}

function ReasonModal({ title, description, placeholder, onCancel, onConfirm, confirmLabel, required }) {
  const [reason, setReason] = useState("");
  const canSubmit = required ? reason.trim().length >= 3 : true;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="admin-compliance-reason-modal"
    >
      <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
        <p className="text-sm text-dapnow-muted mt-1">{description}</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder={placeholder}
          autoFocus
          data-testid="admin-compliance-reason-input"
          className="mt-4 w-full rounded-xl border border-dapnow-line p-3 text-sm focus:border-dapnow-blue focus:ring-2 focus:ring-dapnow-blue/20 outline-none"
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="btn-ghost">Annulla</button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onConfirm(reason.trim() || null)}
            data-testid="admin-compliance-reason-confirm"
            className="btn-primary disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
